import { Button, Heading, Stack, Switch, Text } from '@chakra-ui/react'
import type { SignInSettings as Settings } from '@ipc/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { api, isApiError } from '~/api/client'
import { keys, useSignInSettings } from '~/api/hooks'
import { Fold } from '~/components/member/Fold'
import { FreshSignIn } from '~/components/member/FreshSignIn'
import { isWeakPasswordError, PasswordField } from '~/components/member/PasswordField'
import { ErrorAlert, RetryError, useFriendlyError } from '~/components/member/feedback'
import { FormStack } from '~/components/member/layout'
import { StepSkeleton } from '~/components/member/StepSkeleton'
import { useT } from '~/i18n/use-t'
import { useLogout } from './MemberShell'

const MIN_LENGTH = 8
const BUTTON = { alignSelf: { base: 'stretch', sm: 'flex-start' } } as const

/** This session still counts as a fresh sign-in (10 minutes from it): a first password needs one (P2, P4). */
export const isFresh = (s: Pick<Settings, 'freshSignInUntil'>, now = Date.now()) =>
  s.freshSignInUntil !== null && Date.parse(s.freshSignInUntil) > now

const wrongPassword = (err: unknown) => isApiError(err, 'invalid_credentials')
const needsFresh = (err: unknown) => isApiError(err, 'fresh_sign_in_required')

function Part({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <Stack gap={3} as='section' aria-labelledby={id} borderTop='1px solid' borderColor='border' pt={5}>
      <Heading as='h2' id={id} size='lg'>
        {title}
      </Heading>
      {children}
    </Stack>
  )
}

function PasswordTips() {
  const { t } = useT()
  return (
    <Fold title={t('password.tipsTitle')}>
      <Text>{t('password.tips')}</Text>
    </Fold>
  )
}

/** "Set a password" (no password yet): it needs a fresh sign-in; without one the voter asks for the sign-in email. */
function SetPassword({ s, email, onSaved }: { s: Settings; email: string | null; onSaved: (s: Settings) => void }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const qc = useQueryClient()
  const [password, setPassword] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: () => api('memberPasswordSet', { body: { password } }),
    onSuccess: (next) => {
      setPassword('')
      onSaved(next)
    },
    // The 10 minutes ran out meanwhile: re-read the settings, so the page offers the sign-in email.
    onError: (err) => {
      if (needsFresh(err)) void qc.invalidateQueries({ queryKey: keys.signIn })
    },
  })
  const weak = save.isError && isWeakPasswordError(save.error)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (save.isPending) return
    if ([...password].length < MIN_LENGTH) return setFieldError(t('password.errShort'))
    setFieldError(null)
    save.mutate()
  }
  return (
    <Part id='set-password-title' title={t('signIn.set.title')}>
      <Text>{t('signIn.set.text')}</Text>
      {!isFresh(s) ? (
        <FreshSignIn email={email} mode={s.mode} intro={t('signIn.set.needFresh')} formId='set-password-code' />
      ) : (
        <FormStack gap={4} onSubmit={submit} noValidate>
          <PasswordField
            label={t('signIn.set.label')}
            value={password}
            onChange={(v) => {
              setPassword(v)
              setFieldError(null)
              if (!save.isPending) save.reset()
            }}
            autoComplete='new-password'
            hint={t('password.hintShort')}
            error={fieldError ?? (weak ? t('password.errWeak') : null)}
          />
          <PasswordTips />
          {save.isError && !weak && (
            <ErrorAlert>{needsFresh(save.error) ? t('signIn.set.errFresh') : friendly(save.error)}</ErrorAlert>
          )}
          <Button type='submit' size='lg' {...BUTTON} loading={save.isPending}>
            {t('signIn.set.button')}
          </Button>
        </FormStack>
      )}
    </Part>
  )
}

/** "Change my password": the current one, or nothing within 10 minutes of a sign-in. */
function ChangePassword({ s, onSaved }: { s: Settings; onSaved: (s: Settings) => void }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [currentEmpty, setCurrentEmpty] = useState(false)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const fresh = isFresh(s)
  const change = useMutation({
    mutationFn: () =>
      api('memberPasswordChange', { body: { currentPassword: current || undefined, newPassword: next } }),
    onSuccess: (res) => {
      setCurrent('')
      setNext('')
      onSaved(res)
    },
  })
  const wrongCurrent = change.isError && wrongPassword(change.error)
  const freshGone = change.isError && needsFresh(change.error)
  const refused = change.isError && !wrongCurrent && !freshGone && isWeakPasswordError(change.error)

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (change.isPending) return
    if (!current && !fresh) return setCurrentEmpty(true)
    setCurrentEmpty(false)
    if ([...next].length < MIN_LENGTH) return setFieldError(t('password.errShort'))
    setFieldError(null)
    change.mutate()
  }
  return (
    <Part id='change-password-title' title={t('changePassword.title')}>
      <FormStack gap={4} onSubmit={submit} noValidate>
        <PasswordField
          label={t('changePassword.current')}
          value={current}
          onChange={(v) => {
            setCurrent(v)
            setCurrentEmpty(false)
            if (!change.isPending) change.reset()
          }}
          autoComplete='current-password'
          hint={fresh ? t('signIn.change.currentOptional') : undefined}
          error={
            currentEmpty || freshGone
              ? t('changePassword.errCurrentEmpty')
              : wrongCurrent
                ? t('changePassword.errCurrent')
                : null
          }
        />
        <PasswordField
          label={t('changePassword.new')}
          value={next}
          onChange={(v) => {
            setNext(v)
            setFieldError(null)
          }}
          autoComplete='new-password'
          hint={t('password.hintShort')}
          error={fieldError ?? (refused ? t('changePassword.errNew') : null)}
        />
        <PasswordTips />
        {change.isError && !wrongCurrent && !refused && !freshGone && <ErrorAlert>{friendly(change.error)}</ErrorAlert>}
        <Button type='submit' size='lg' {...BUTTON} loading={change.isPending}>
          {t('changePassword.button')}
        </Button>
      </FormStack>
    </Part>
  )
}

/**
 * The switch "Also ask for a code sent to my email when I sign in with my password". On: the session is enough. Off
 * (P4): the current password, asked under the switch before anything changes.
 */
function EmailCodeSwitch({ s, onSaved }: { s: Settings; onSaved: (s: Settings) => void }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const on = s.mode === 'password_and_email'
  const [confirmOff, setConfirmOff] = useState(false)
  const [password, setPassword] = useState('')
  const [empty, setEmpty] = useState(false)
  const save = useMutation({
    mutationFn: (enabled: boolean) =>
      api('memberEmailCodeSetting', { body: enabled ? { enabled } : { enabled, currentPassword: password } }),
    onSuccess: (res) => {
      setConfirmOff(false)
      setPassword('')
      onSaved(res)
    },
  })
  const wrong = save.isError && wrongPassword(save.error)
  const submitOff = (e: FormEvent) => {
    e.preventDefault()
    if (save.isPending) return
    if (!password) return setEmpty(true)
    setEmpty(false)
    save.mutate(false)
  }
  return (
    <Part id='email-code-title' title={t('signIn.code.title')}>
      <Switch.Root
        checked={on}
        disabled={save.isPending || confirmOff}
        alignItems='flex-start'
        onCheckedChange={(e) => {
          save.reset()
          if (e.checked) save.mutate(true)
          else setConfirmOff(true)
        }}
      >
        <Switch.HiddenInput />
        <Switch.Control mt={1} />
        <Switch.Label fontWeight='normal'>{t('signIn.code.label')}</Switch.Label>
      </Switch.Root>
      <Text color='fg.muted'>{t(on ? 'signIn.code.onText' : 'signIn.code.offText')}</Text>
      {confirmOff && (
        <FormStack gap={4} onSubmit={submitOff} noValidate>
          <Text>{t('signIn.code.confirmOff')}</Text>
          <PasswordField
            label={t('signIn.passwordLabel')}
            value={password}
            onChange={(v) => {
              setPassword(v)
              setEmpty(false)
              if (!save.isPending) save.reset()
            }}
            autoComplete='current-password'
            error={empty ? t('changePassword.errCurrentEmpty') : wrong ? t('changePassword.errCurrent') : null}
          />
          {save.isError && !wrong && <ErrorAlert>{friendly(save.error)}</ErrorAlert>}
          <Stack direction={{ base: 'column', sm: 'row' }} gap={3}>
            <Button type='submit' size='lg' loading={save.isPending}>
              {t('signIn.code.turnOff')}
            </Button>
            <Button
              type='button'
              size='lg'
              variant='outline'
              disabled={save.isPending}
              onClick={() => {
                setConfirmOff(false)
                setPassword('')
                setEmpty(false)
                save.reset()
              }}
            >
              {t('signIn.cancel')}
            </Button>
          </Stack>
        </FormStack>
      )}
      {!confirmOff && save.isError && <ErrorAlert>{friendly(save.error)}</ErrorAlert>}
    </Part>
  )
}

/** "Remove my password" (P4: the current password): back to the sign-in email only. */
function RemovePassword({ onSaved }: { onSaved: (s: Settings) => void }) {
  const { t } = useT()
  const friendly = useFriendlyError()
  const [password, setPassword] = useState('')
  const [empty, setEmpty] = useState(false)
  const remove = useMutation({
    mutationFn: () => api('memberPasswordRemove', { body: { currentPassword: password } }),
    onSuccess: (res) => {
      setPassword('')
      onSaved(res)
    },
  })
  const wrong = remove.isError && wrongPassword(remove.error)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (remove.isPending) return
    if (!password) return setEmpty(true)
    setEmpty(false)
    remove.mutate()
  }
  return (
    <Part id='remove-password-title' title={t('signIn.remove.title')}>
      <Text>{t('signIn.remove.text')}</Text>
      <FormStack gap={4} onSubmit={submit} noValidate>
        <PasswordField
          label={t('signIn.passwordLabel')}
          value={password}
          onChange={(v) => {
            setPassword(v)
            setEmpty(false)
            if (!remove.isPending) remove.reset()
          }}
          autoComplete='current-password'
          error={empty ? t('changePassword.errCurrentEmpty') : wrong ? t('changePassword.errCurrent') : null}
        />
        {remove.isError && !wrong && <ErrorAlert>{friendly(remove.error)}</ErrorAlert>}
        <Button type='submit' size='lg' variant='outline' {...BUTTON} loading={remove.isPending}>
          {t('signIn.remove.button')}
        </Button>
      </FormStack>
    </Part>
  )
}

/**
 * The "Sign-in" section of the voter page (passwordless spec §4, rulings P3, P4): how the voter signs in now, in plain
 * words; set a first password, or change it, remove it and switch the emailed code on or off; and the one "Sign out",
 * which ends every session. Every change answers the new settings, which replace the cached ones.
 */
export function SignInSettings({ email }: { email: string | null }) {
  const { t } = useT()
  const qc = useQueryClient()
  const settings = useSignInSettings()
  const logout = useLogout()
  const [done, setDone] = useState<string | null>(null)
  const saved = (message: string) => (next: Settings) => {
    qc.setQueryData(keys.signIn, next)
    setDone(message)
  }

  let body: ReactNode
  if (settings.isPending) body = <StepSkeleton />
  else if (settings.isError) body = <RetryError error={settings.error} onRetry={() => void settings.refetch()} />
  else {
    const s = settings.data
    body = (
      <>
        <Text data-sign-in-mode={s.mode}>{t(`signIn.mode.${s.mode}`)}</Text>
        {done && <Text role='status'>{done}</Text>}
        {s.hasPassword ? (
          <>
            <ChangePassword s={s} onSaved={saved(t('changePassword.done'))} />
            <EmailCodeSwitch s={s} onSaved={(next) => saved(t(`signIn.code.done.${next.mode}`))(next)} />
            <RemovePassword onSaved={saved(t('signIn.remove.done'))} />
          </>
        ) : (
          <SetPassword s={s} email={email} onSaved={saved(t('signIn.set.done'))} />
        )}
      </>
    )
  }
  return (
    <Stack gap={5} as='section' aria-labelledby='sign-in-title'>
      <Heading as='h1' id='sign-in-title' size={{ base: '2xl', md: '3xl' }}>
        {t('signIn.title')}
      </Heading>
      {body}
      {logout && (
        <Part id='sign-out-title' title={t('signIn.signOut.title')}>
          <Text>{t('signIn.signOut.text')}</Text>
          <Button variant='outline' size='lg' {...BUTTON} loading={logout.isPending} onClick={() => logout.mutate()}>
            {t('dashboard.logOut')}
          </Button>
        </Part>
      )}
    </Stack>
  )
}
